import create from 'zustand';

// ── Arena tournaments (tournament_type === 'Circlechess_Arena') ─────────────
// Transient socket-fed state for the arena surface. Written exclusively by the
// arena_* message handlers in PlayChess/index.js (status / standings /
// berserk) plus the isArena flag TournamentPlay derives from tournament
// details; read by ArenaLobby / ArenaBerserkControl. Deliberately NOT
// persisted — every field is server-authoritative and refetched via
// arena_status_request on mount/reconnect.

export interface ArenaStatus {
  // 'created' | 'running' | 'finishing' | 'finalizing' | 'finished'
  state: string;
  // 'pooled' | 'paired' | 'paused' | 'idle'
  myState: string;
  // pause-after-this-game flag: true while paused even when myState is
  // still 'paired' (pause only prevents re-pooling, the live game continues)
  paused?: boolean;
  endsAtMs: number;
  poolSize: number;
  points: number;
  streak: number;
  onFire: boolean;
  berserkEnabled: boolean;
  // ── per-user, and the reason the sticky "your rank" row needs no REST ──
  // The standings broadcast is per-ROOM so it can never carry a per-caller
  // row; status is 1:1, so these are how a player learns their own position
  // no matter which page they are browsing. 0/absent = never joined.
  rank?: number;
  rating?: number;
  gamesPlayed?: number;
  // full roster size (ZCARD live / COUNT archived) — pager + "#N of M"
  total?: number;
  // true when the arena's redis keys have been TTL'd and this was served
  // from postgres. The arena is immutable at that point.
  archived?: boolean;
}

// Liveness of a roster member, stamped on each standings entry by the server:
//   playing = holds an arena_live lock; waiting = in the pairing pool;
//   paused = pstate paused=1; online = connected but none of the above;
//   offline = not connected. Absent on older payloads — render nothing then.
export type ArenaPresence =
  | 'playing'
  | 'waiting'
  | 'paused'
  | 'online'
  | 'offline';

export interface ArenaStandingEntry {
  uid: string;
  username: string;
  // GLOBAL rank (offset-aware), not the row's index within the page.
  rank: number;
  rating?: number;
  points: number;
  streak: number;
  onFire: boolean;
  gamesPlayed: number;
  wins: number;
  longestStreak: number;
  presence?: ArenaPresence;
  // Chronological per-game points (oldest→newest), capped to the last 12
  // games (2=win, 1=draw, 4=doubled/on-fire win, 5=doubled win + berserk,
  // 0=loss/quick-draw). Absent on older payloads — render nothing then.
  scores?: number[];
}

// Presence tallies over the FULL roster (not just the topN entries shipped in
// `entries`). Note: paused members are in none of these buckets by contract.
export interface ArenaCounts {
  playing: number;
  waiting: number;
  online: number;
  offline: number;
}

export interface ArenaStandings {
  entries: ArenaStandingEntry[];
  state: string;
  endsAtMs: number;
  counts?: ArenaCounts;
  // TRUE roster size, independent of how many entries were shipped — the
  // broadcast only carries the top N. Sizes the pager. Absent on older
  // payloads → fall back to entries.length.
  total?: number;
}

interface ArenaState {
  // Flipped by TournamentPlay when tournament details report
  // tournament_type === 'Circlechess_Arena'. PlayChess keys every arena
  // touchpoint (sends, handlers, berserk button, game-end return) off this.
  isArena: boolean;
  // Whether the logged-in user has joined THIS tournament — set by
  // TournamentPlay from details (is_joined) and kept true after a successful
  // in-session join. Gates the auto-resume send in PlayChess so mere
  // visitors are never pooled.
  isJoined: boolean;
  status: ArenaStatus | null;
  standings: ArenaStandings | null;
  // Per-game berserk flags for the table I'm seated at. Reset between games
  // via resetGameBerserk() (start_game_response / game_end_response).
  myBerserk: boolean;
  opponentBerserk: boolean;
}

interface ArenaActions {
  setIsArena: (val: boolean) => void;
  setIsJoined: (val: boolean) => void;
  // The server pushes PARTIAL status payloads (e.g. {state:'finishing'} on
  // the finish broadcast), so setStatus MERGES into the previous status —
  // replacing wholesale wiped myState/points/streak/onFire/berserkEnabled
  // (which hid the berserk button for the whole first game). resetArena is
  // the only full clear.
  setStatus: (status: Partial<ArenaStatus> | null) => void;
  setStandings: (standings: ArenaStandings | null) => void;
  // payload is the parsed arena_berserk_response payload:
  //   success (table broadcast): {userId, newClockMs, incrementMs}
  //   refusal (1:1):             {userId, refused: 'reason'}
  // myUserId lets the store decide which side's flag to raise.
  setBerserk: (
    userId: string | number,
    payload: {refused?: string; newClockMs?: number; incrementMs?: number},
    myUserId?: string | number,
  ) => void;
  resetGameBerserk: () => void;
  resetArena: () => void;
}

const useArenaStore = create<ArenaState & ArenaActions>(set => ({
  isArena: false,
  isJoined: false,
  status: null,
  standings: null,
  myBerserk: false,
  opponentBerserk: false,

  setIsArena: val => set({isArena: val}),
  setIsJoined: val => set({isJoined: val}),
  setStatus: status =>
    set(st => ({
      status:
        status === null ? null : ({...st.status, ...status} as ArenaStatus),
    })),
  setStandings: standings => set({standings}),
  setBerserk: (userId, payload, myUserId) => {
    if (payload?.refused) {
      // Refusal is informational (window closed / not-arena / error); the
      // button stays visible because myBerserk was never raised.
      return;
    }
    if (myUserId !== undefined && String(userId) === String(myUserId)) {
      set({myBerserk: true});
    } else {
      set({opponentBerserk: true});
    }
  },
  resetGameBerserk: () => set({myBerserk: false, opponentBerserk: false}),
  resetArena: () =>
    set({
      isArena: false,
      isJoined: false,
      status: null,
      standings: null,
      myBerserk: false,
      opponentBerserk: false,
    }),
}));

export default useArenaStore;
